import React, { useEffect, useState } from 'react';
import 'highlight.js';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import PrivateWrapper from '../../layouts/Private';
import 'react-quill/dist/quill.snow.css';
import EditorToolbar, { redoChange, undoChange } from '../../components/blogs/EditorToolbar';
import useToastr from '../../hooks/useToastr';
import { createBlog, updateBlog, viewBlogById } from '../../services/Blogs';
import { blogStatus } from '../../configs/Constants';

const Create = () => {
  const pageName = 'Create Blog';
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToastr, showSuccessToastr } = useToastr();
  const [value, setValue] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [imageFile, setImageFile] = useState(null); // Store the selected image file here
  const [imageUrl, setImageUrl] = useState(''); // Store the image URL for preview and update

  const onChange = (v) => {
    setValue(v);
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
    setImageUrl(URL.createObjectURL(e.target.files[0])); // Set the preview image URL
  };

  const handleSubmit = async () => {
    try {
      if (id && id !== '') {
        const storage = getStorage();

        let newImageUrl = '';

        // Upload a new image to Firebase Storage only if an image is selected
        if (imageFile) {
          const storageRef = ref(storage, `blog-covers/${Date.now()}-${imageFile.name}`);
          await uploadBytes(storageRef, imageFile);
          newImageUrl = await getDownloadURL(storageRef);
        } else {
          newImageUrl = imageUrl; // Use the existing image URL if no new image is selected
        }

        await updateBlog(id, {
          title,
          content: value,
          description,
          status: status ? blogStatus.PUBLISHED : blogStatus.DRAFT,
          coverImage: newImageUrl,
        });
        showSuccessToastr('Blog updated successfully.');
      } else {
        const storage = getStorage();

        let newImageUrl = '';

        // Upload a new image to Firebase Storage only if an image is selected
        if (imageFile) {
          const storageRef = ref(storage, `blog-covers/${Date.now()}-${imageFile.name}`);
          await uploadBytes(storageRef, imageFile);
          newImageUrl = await getDownloadURL(storageRef);
        } else {
          newImageUrl = imageUrl; // Use the existing image URL if no new image is selected
        }

        await createBlog({
          title,
          content: value,
          description,
          status: status ? blogStatus.PUBLISHED : blogStatus.DRAFT,
          coverImage: newImageUrl,
        });
        showSuccessToastr('Blog created successfully.');
      }

      setValue('');
      setStatus(false);
      setTitle('');
      setDescription('');
      setImageFile(null);
      navigate(-1);
    } catch (error) {
      showErrorToastr(error?.message || 'Something went wrong.');
    }
  };

  const modules = {
    toolbar: {
      container: `#blog-editor`,
      handlers: {
        undo: undoChange,
        redo: redoChange,
      },
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },

    clipboard: {
      matchVisual: false,
    },
  };

  useEffect(() => {
    if (id && id !== '') {
      setDataLoaded(false);
      viewBlogById(id)
        .then((res) => {
          setValue(res.content);
          setStatus(res.status === blogStatus.PUBLISHED);
          setTitle(res.title);
          setDescription(res.description);
          setDataLoaded(true);
          setImageUrl(res.coverImage || '');
        })
        .catch((e) => {
          showErrorToastr(e?.message || 'Something went wrong.');
          navigate(-1);
        });
    } else {
      setDataLoaded(true);
    }
  }, [id]);

  return (
    <PrivateWrapper pageName={pageName}>
      <Container maxWidth="md" px={2}>
        {!dataLoaded ? (
          <Card sx={{ py: 3, my: 3, px: 2 }} variant="outlined">
            <Box
              display="flex"
              minHeight="60vh"
              justifyContent="center"
              mt={2}
              gap={5}
              alignItems="center"
            >
              <CircularProgress size={24} />
            </Box>
          </Card>
        ) : (
          <Card sx={{ py: 3, my: 3, px: 2 }} variant="outlined">
            <Box display="flex" flexDirection="column" my={4} gap={2} alignItems="center">
              <>
                <Box width="100%">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Blog Cover"
                      style={{ width: '100%', height: '60vh' }}
                    />
                  )}
                  <TextField
                    id="blogCover"
                    fullWidth
                    name="blogCover"
                    focused
                    label="Blog Cover"
                    variant="outlined"
                    type="file"
                    margin="dense"
                    InputLabelProps={{ shrink: true }} // Ensure the label doesn't overlap
                    InputProps={{
                      accept: 'image/*', // Specify that only image files are accepted
                      inputProps: {
                        accept: 'image/*', // Specify that only image files are accepted
                        multiple: false, // Allow only a single file to be selected
                      },
                    }}
                    onChange={handleImageChange}
                  />
                </Box>
                <TextField
                  required
                  id="title"
                  fullWidth
                  name="title"
                  label="Blog title"
                  variant="outlined"
                  type="text"
                  margin="dense"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <TextField
                  required
                  id="description"
                  name="description"
                  label="Description"
                  fullWidth
                  multiline
                  minRows={5}
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </>
            </Box>

            <EditorToolbar id="blog-editor" />
            <ReactQuill
              value={value}
              modules={modules}
              onChange={onChange}
              placeholder="Write something awesome..."
            />
            <Box display="flex" mt={2} gap={5} alignItems="center">
              <Box sx={{ flexGrow: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={status}
                    onChange={(event) => {
                      setStatus(event.target.checked);
                    }}
                  />
                }
                label="Publish"
                labelPlacement="start"
              />
              <Box ml="auto">
                <Button variant="contained" disableElevation onClick={handleSubmit}>
                  Save
                </Button>
              </Box>
            </Box>
          </Card>
        )}
      </Container>
    </PrivateWrapper>
  );
};

export default Create;
